import { useCallback, useEffect, useRef, useState } from 'react';

import { FilesFolders } from 'constants/files-folders-enum';
import { now } from 'helpers/date';
import { clearHiddenFieldValues } from 'helpers/field-config/form-page-helpers';
import { processFields } from 'helpers/field-config/process-static-content';
import { snakeToTitleCase } from 'helpers/text';
import { useApiUploadActions } from 'hooks/upload-actions';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import FieldConfigRenderer from 'components/FieldConfigRenderer/FieldConfigRenderer';
import {
  Button,
  DatePicker,
  FilePicker,
  FormMessage,
  Input,
  InputGroup,
  MessageType,
  ToggleSwitch,
  ToggleSwitchInput,
} from 'components/FormComponents';

import { useLazyGetResolvedCheckTypeQuery } from 'api/candidate-checks-api';

/**
 * ============================================================================
 * ResultUploadSection Component
 * ============================================================================
 **/
const ResultUploadSection = ({
  companyId,
  control,
  check,
  metadata,
  getUpload = () => {},
  signUpload = () => {},
  createUpload = () => {},
  deleteUpload = () => {},
  allowedTypes = { 'application/pdf': ['.pdf'] },
  result = false,
  title,
}) => (
  <InputGroup title={title}>
    <Controller
      control={control}
      name="upload_id"
      render={({ field: { onChange, value } }) => (
        <FilePicker
          onChange={onChange}
          value={value}
          metadata={metadata}
          accept={allowedTypes}
          companyId={companyId}
          modelParams={{
            candidate_id: check.candidate_id,
            candidate_check_id: check.id,
            ...(result && { result: true }),
          }}
          bucketFolder={check.type === 'acic_check' ? FilesFolders.Acic : FilesFolders.ResultFiles}
          getUpload={getUpload}
          signUpload={signUpload}
          createUpload={createUpload}
          deleteUpload={deleteUpload}
        />
      )}
    />
  </InputGroup>
);

/**
 * ============================================================================
 * ExtraDataReadOnlyDisplay Component
 * ============================================================================
 * Read-only display component for extra_data when check is complete.
 * Shows all extra_data values with labels from extra_result_details where available.
 */
const ExtraDataReadOnlyDisplay = ({ extraData, extraResultDetails }) => {
  const { t } = useTranslation();

  const safeExtraData = extraData ?? {};
  const safeExtraResultDetails = extraResultDetails ?? [];

  const extraDataKeys = Object.keys(safeExtraData);

  if (extraDataKeys.length === 0) {
    return (
      <div className="secondary-text u-font-size-small">
        {t('admin.candidate.completionModal.noExtraData', 'No additional data recorded')}
      </div>
    );
  }

  // Build a map of field codes to their config from extra_result_details
  const fieldConfigMap = safeExtraResultDetails.reduce((acc, detail) => {
    if (detail?.code) {
      acc[detail.code] = detail;
    }
    return acc;
  }, {});

  // Format field name for display (convert snake_case to Title Case)
  // Falls back to snakeToTitleCase helper if field config doesn't have a title
  const formatFieldName = key => {
    if (!key || typeof key !== 'string') return 'Unknown Field';
    return snakeToTitleCase(key);
  };

  // Render value - similar to CandidateCheckValues.jsx renderValue
  const renderValue = value => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'true' : 'false';

    if (Array.isArray(value)) {
      if (value.length === 0) return '-';
      // Array of strings - display as comma-separated or list
      if (value.every(v => typeof v === 'string' || v === null || v === undefined)) {
        const filtered = value.filter(v => v !== null && v !== undefined);
        return filtered.length > 0 ? filtered.join(', ') : '-';
      }
      // Array of objects - stringify
      return JSON.stringify(value);
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  return (
    <div className="d-flex flex-column u-gap-small">
      {extraDataKeys.map(key => {
        const value = safeExtraData[key];
        const fieldConfig = fieldConfigMap[key];
        const title = fieldConfig?.title ?? formatFieldName(key);

        return (
          <div key={key} className="d-flex flex-column">
            <span className="secondary-text u-font-size-small u-font-weight-600 u-capitalize">
              {title}
            </span>
            <span className="primary-text">{renderValue(value)}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * ============================================================================
 * CandidateCompletionModal Component
 * ============================================================================
 **/
export const CandidateCompletionModal = ({
  companyId,
  company = null,
  checkType,
  companyCheck = null,
  check,
  subCheck = null,
  candidate = null,
  candidateValues = null,
  visible,
  onUpdate = () => {},
  onClose = () => {},
  showPauseToggle = false,
}) => {
  const { t } = useTranslation();

  // ============================================================================
  // Component Setup - Basic State and Helpers
  // ============================================================================
  const isSubCheck = !!subCheck;
  const record = isSubCheck ? subCheck : check;
  const extraDataKey = isSubCheck ? 'extra_details' : 'extra_data';

  const { getUpload, signUpload, createUpload, deleteUpload } = useApiUploadActions();

  // ============================================================================
  // Component State - UI Toggles
  // ============================================================================
  const [manualResultUploadEnabled, setManualResultUploadEnabled] = useState(false);
  const [supportingDocumentUploadEnabled, setSupportingDocumentUploadEnabled] = useState(false);
  const [supportingDocumentUploadId, setSupportingDocumentUploadId] = useState(null);
  const [selectedOptionsContext, setSelectedOptionsContext] = useState({});

  useEffect(() => {
    setSelectedOptionsContext({});
  }, [check?.id, subCheck?.id]);

  // ============================================================================
  // FieldConfigRenderer Setup
  // ============================================================================
  // State for hidden field elements - updated only when field count changes to avoid loops
  const [hiddenFieldElements, setHiddenFieldElements] = useState([]);
  const hiddenFieldCountRef = useRef(0);

  // Callback for hidden fields - only update state if count changes to avoid infinite loops
  const handleHiddenFieldsRender = useCallback(elements => {
    const newCount = elements?.length || 0;
    if (newCount !== hiddenFieldCountRef.current) {
      hiddenFieldCountRef.current = newCount;
      setHiddenFieldElements(elements);
    }
  }, []);

  // ============================================================================
  // Data Fetching - Resolved Check Type
  // ============================================================================
  // Fetch fully resolved check type when modal opens
  const [getResolvedCheckType, { data: resolvedCheckType, isLoading: isLoadingResolvedCheckType }] =
    useLazyGetResolvedCheckTypeQuery();

  useEffect(() => {
    if (visible && check?.id) {
      getResolvedCheckType({
        id: check.id,
        subCheckId: subCheck?.id,
      });
    }
  }, [visible, check?.id, subCheck?.id, getResolvedCheckType]);

  // Use resolved check type if available, otherwise fall back to passed checkType
  const effectiveCheckType = resolvedCheckType || checkType;

  // Check if there are any fields (visible or hidden)
  const hasFields = effectiveCheckType?.extra_result_details?.length > 0;

  // ============================================================================
  // Form Setup
  // ============================================================================
  // Build form default values from check/subCheck data.
  // Seed hidden field defaults from resolved extra_result_details so {{fieldCode}} in section_help_text resolves on first render.
  const buildFormDefaults = () => {
    const saved = record?.[extraDataKey] ?? {};
    const extra = { ...saved };

    (effectiveCheckType?.extra_result_details ?? []).forEach(field => {
      if (field?.type !== 'hidden') return;
      const code = field.code;
      if (code == null || code in extra) return;
      const dv = field.default_value ?? field.defaultValue;
      if (dv !== undefined && dv !== null) {
        extra[code] = dv;
      }
    });

    return {
      has_report: !!effectiveCheckType?.has_report,
      upload_id: record?.upload_id,
      result_flag: !!record?.result_flag,
      provider_id: record?.provider_id,
      expiration_date: record?.expiration_date || '9999-12-31',
      [extraDataKey]: extra,
    };
  };

  const {
    control,
    register,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: buildFormDefaults(),
  });

  useEffect(() => {
    reset(buildFormDefaults());
    setSupportingDocumentUploadId(null);
  }, [check, subCheck, effectiveCheckType, reset]);

  // ============================================================================
  // Form Submission and Data Persistence
  // ============================================================================
  // Persist everything currently in the form
  const persistAll = (patch = {}, generateReport = false) => {
    const snapshot = { ...watch(), ...patch };
    const normalized = {};

    if ('upload_id' in snapshot && snapshot.upload_id != null) {
      normalized.upload_id = snapshot.upload_id;
    }

    if (effectiveCheckType?.has_result_flag && 'result_flag' in snapshot) {
      normalized.result_flag = !!snapshot.result_flag;
    }

    if (effectiveCheckType?.has_provider_id && 'provider_id' in snapshot) {
      normalized.provider_id = snapshot.provider_id;
    }

    if (effectiveCheckType?.has_expiration_date && 'expiration_date' in snapshot) {
      normalized.expiration_date =
        snapshot.expiration_date === '' ? null : snapshot.expiration_date;
    }

    if (extraDataKey in snapshot) {
      const originalExtra = record?.[extraDataKey] ?? {};
      if (record.completed_at) {
        if (Object.keys(originalExtra).length > 0) {
          normalized[extraDataKey] = originalExtra;
        }
      } else {
        const snapshotExtra = snapshot[extraDataKey] || {};
        normalized[extraDataKey] = {
          ...originalExtra,
          ...snapshotExtra,
        };
      }
    }

    if ('completed_at' in snapshot) {
      normalized.completed_at = snapshot.completed_at;
    }

    onUpdate(normalized, generateReport);
  };

  const createSupportingDocumentUpload = async uploadData => {
    const result = await createUpload(uploadData);
    return result;
  };

  // Helper to clear hidden field values before submission/save
  const clearHiddenFields = () => {
    if (effectiveCheckType?.extra_result_details?.length > 0) {
      clearHiddenFieldValues(
        processFields(effectiveCheckType.extra_result_details),
        getValues,
        setValue,
        extraDataKey,
        {
          company: company ?? null,
          companyCheckConfig: companyCheckConfig,
          candidate: candidateData,
          checkDetails: checkDetailsData,
          subCheckDetails: subCheckDetailsData,
          candidateValues: candidateValues ?? null,
          selectedOptionsContext,
        },
      );
    }
  };

  const onSubmit = data => {
    clearHiddenFields();
    if (!record.completed_at) data.completed_at = now();
    persistAll(data);
  };

  const saveDetails = () => {
    clearHiddenFields();
    persistAll({}, false);
  };

  const saveDetailsAndGenerateReport = () => {
    clearHiddenFields();
    persistAll({}, true);
  };

  const handleClose = () => {
    onClose();
  };

  // ============================================================================
  // FieldConfigRenderer Setup - Context
  // ============================================================================
  const companyCheckConfig = companyCheck?.configuration || null;
  const candidateData = candidate || null;
  const checkDetailsData = check?.details || null;
  const subCheckDetailsData = subCheck?.details || null;

  return (
    <AnimatedModal
      visible={visible}
      onClose={handleClose}
      title={`Complete ${record?.title || effectiveCheckType?.title}`}
      icon="check"
      small>
      {/* ============================================================================ */}
      {/* Document Request Pause Toggle (Main Checks Only) */}
      {/* ============================================================================ */}
      {!isSubCheck && showPauseToggle && !watch('upload_id') && (
        <Controller
          control={control}
          name="extra_data.document_request_paused"
          render={({ field: { onChange, value } }) => (
            <ToggleSwitch
              label={t('admin.candidate.completionModal.pauseAutomatedDocumentRequests')}
              inputProps={{
                checked: !!value,
                onChange: e => {
                  const checked = e.target.checked;
                  const currentExtraData = watch('extra_data') || {};
                  const updatedExtraData = {
                    ...currentExtraData,
                    document_request_paused: checked,
                  };
                  onChange(checked);
                  setValue('extra_data', updatedExtraData);
                  persistAll({ extra_data: updatedExtraData }, false);
                },
              }}
              status={
                value
                  ? { text: t('admin.candidate.completionModal.paused'), status: 'warning' }
                  : { text: t('admin.candidate.completionModal.active'), status: 'success' }
              }
            />
          )}
        />
      )}
      <ToggleSwitch
        name="has_report"
        register={register}
        label={t('admin.candidate.completionModal.reportType')}
        status={
          watch('has_report')
            ? { text: t('admin.candidate.completionModal.automaticReport'), status: 'neutral' }
            : { text: t('admin.candidate.completionModal.manualReport'), status: 'neutral' }
        }
      />
      {/* NOTE: Difference from Manual Report Upload:
       * - Supporting docs: metadata uses {check_type, sub_check_id}, NO result:true in modelParams
       * - Manual report: metadata uses {result/sub_result, sub_check_id}, HAS result:true in modelParams
       * - Supporting docs: stored in local state (supportingDocumentUploadId), NOT saved to check.upload_id
       * - Manual report: stored in form field (upload_id), saved to check.upload_id via form submission
       * - Supporting docs: accepts PDF and images; Manual report: PDF only
       */}
      {watch('has_report') && (
        <>
          <ToggleSwitchInput
            text={t('admin.candidate.completionModal.uploadSupportingDocument')}
            subtext={t('admin.candidate.completionModal.uploadSupportingDocumentDescription')}
            onChange={e => setSupportingDocumentUploadEnabled(e.target.checked)}
            className="u-margin-top"
            noMaxWidth
            value={supportingDocumentUploadEnabled}>
            <InputGroup title={t('admin.candidate.completionModal.attachSupportingDocument')}>
              <FilePicker
                onChange={uploadId => {
                  setSupportingDocumentUploadId(uploadId);
                }}
                value={supportingDocumentUploadId}
                metadata={{
                  ...(isSubCheck
                    ? { check_type: check.type, sub_check_id: subCheck.id }
                    : { check_type: check.type }),
                }}
                accept={{ 'application/pdf': ['.pdf'], 'image/*': ['.jpeg', '.png'] }}
                modelParams={{
                  candidate_id: check.candidate_id,
                  candidate_check_id: check.id,
                  // NOTE: NO result:true here - this is a supporting document, not the result file
                }}
                bucketFolder={
                  check.type === 'acic_check' ? FilesFolders.Acic : FilesFolders.ResultFiles
                }
                getUpload={getUpload}
                signUpload={signUpload}
                createUpload={createSupportingDocumentUpload}
                deleteUpload={deleteUpload}
              />
            </InputGroup>
          </ToggleSwitchInput>
        </>
      )}

      {/* ============================================================================ */}
      {/* Main Form */}
      {/* ============================================================================ */}
      {visible && (
        <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
          {/* ============================================================================ */}
          {/* Manual Report Upload (Manual Reports Only) */}
          {/* ============================================================================ */}
          {/* NOTE: Difference from Supporting Document Upload:
           * - Manual report: metadata uses {result/sub_result, sub_check_id}, HAS result:true in modelParams
           * - Supporting docs: metadata uses {check_type, sub_check_id}, NO result:true in modelParams
           * - Manual report: stored in form field (upload_id), saved to check.upload_id via form submission
           * - Supporting docs: stored in local state (supportingDocumentUploadId), NOT saved to check.upload_id
           * - Manual report: PDF only; Supporting docs: PDF and images
           */}
          {!watch('has_report') && (
            <ToggleSwitchInput
              text={t('admin.candidate.completionModal.uploadReportManually')}
              subtext={t('admin.candidate.completionModal.uploadReportManuallyDescription')}
              onChange={e => setManualResultUploadEnabled(e.target.checked)}
              noMaxWidth
              value={manualResultUploadEnabled}>
              {manualResultUploadEnabled && (
                <ResultUploadSection
                  companyId={companyId}
                  control={control}
                  check={check}
                  metadata={{
                    ...(isSubCheck
                      ? { sub_result: check.type, sub_check_id: subCheck.id }
                      : { result: check.type }),
                  }}
                  result={true}
                  // NOTE: result={true} adds result:true to modelParams, marking this as the result file
                  getUpload={getUpload}
                  signUpload={signUpload}
                  createUpload={createUpload}
                  deleteUpload={deleteUpload}
                  title={t('admin.candidate.completionModal.uploadReportManuallyTitle')}
                />
              )}
            </ToggleSwitchInput>
          )}

          {/* ============================================================================ */}
          {/* Result Flag Toggle */}
          {/* ============================================================================ */}
          {effectiveCheckType?.has_result_flag && (
            <ToggleSwitch
              name="result_flag"
              register={register}
              label={t('admin.candidate.completionModal.hasResult')}
              status={
                watch('result_flag')
                  ? {
                      text:
                        effectiveCheckType?.true_flag_label ||
                        t('admin.candidate.completionModal.resultFound'),
                      status: 'error',
                    }
                  : {
                      text:
                        effectiveCheckType?.false_flag_label ||
                        t('admin.candidate.completionModal.noResultFound'),
                      status: 'success',
                    }
              }
            />
          )}

          {/* ============================================================================ */}
          {/* Provider ID Input */}
          {/* ============================================================================ */}
          {effectiveCheckType?.has_job && effectiveCheckType?.has_provider_id && (
            <InputGroup title={t('admin.candidate.completionModal.providerId')}>
              <Input
                name="provider_id"
                type="text"
                placeholder={t('admin.candidate.completionModal.enterProviderId')}
                defaultValue={check?.provider_id}
                register={register}
                onBlur={() => persistAll()}
              />
            </InputGroup>
          )}

          {/* ============================================================================ */}
          {/* Additional Details Section */}
          {/* ============================================================================ */}
          {(effectiveCheckType?.has_expiration_date || hasFields) && (
            <div className="u-border-radius background-primary u-border u-padding u-padding-top d-flex flex-column">
              <span className="primary-text u-font-size-x-large u-font-weight-600">
                {t('admin.candidate.completionModal.additionalDetails')}
              </span>

              {/* Expiration Date Input */}
              {effectiveCheckType?.has_expiration_date && (
                <InputGroup title={t('admin.candidate.completionModal.expirationDate')}>
                  <Controller
                    control={control}
                    name="expiration_date"
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        value={value}
                        onChange={onChange}
                        outputFormat="YYYY-MM-DD"
                        useDefaultToday={false}
                      />
                    )}
                  />
                </InputGroup>
              )}

              {/* ============================================================================ */}
              {/* FieldConfigRenderer Block */}
              {/* Hidden fields: register with form outside wrapper to avoid triggering Collapse */}
              {/* ============================================================================ */}
              {hiddenFieldElements}

              {/* Read-only display when check is already complete */}
              {record.completed_at ? (
                <ExtraDataReadOnlyDisplay
                  extraData={record?.[extraDataKey]}
                  extraResultDetails={effectiveCheckType?.extra_result_details}
                />
              ) : hasFields && isLoadingResolvedCheckType ? (
                /* Wait for resolved check type so options (e.g. provider with extend_fields) are available;
                 * otherwise statement fields like provider_details show raw {{provider.send_method}} until refetch. */
                <div className="secondary-text u-font-size-small">
                  {t('common.loading', 'Loading...')}
                </div>
              ) : (
                /* Editable form fields when check is not yet complete */
                <FieldConfigRenderer
                  fields={effectiveCheckType?.extra_result_details}
                  control={control}
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                  watch={watch}
                  formValuesPrefix={extraDataKey}
                  errors={errors}
                  company={company ?? null}
                  companyCheckConfig={companyCheckConfig}
                  candidate={candidateData}
                  checkDetails={checkDetailsData}
                  subCheckDetails={subCheckDetailsData}
                  candidateValues={candidateValues ?? null}
                  hideOptionalTag={true}
                  onHiddenFieldsRender={handleHiddenFieldsRender}
                  onSelectedOptionsContextChange={setSelectedOptionsContext}
                />
              )}
            </div>
          )}
          {/* ============================================================================ */}
          {/* FieldConfigRenderer Block - END */}
          {/* ============================================================================ */}

          {/* Only show Update Details button when check is not complete and has editable fields */}
          {!record.completed_at &&
            (effectiveCheckType?.extra_result_details?.length > 0 ||
              effectiveCheckType?.has_provider_id) && (
              <div className="d-flex u-flex-wrap u-gap-medium u-flex-align-center u-margin-top">
                <Button
                  type="secondary"
                  large
                  className="flex-grow-1"
                  onClick={() => saveDetails()}>
                  {t('admin.candidate.completionModal.updateDetails')}
                </Button>
              </div>
            )}

          <div className="d-flex u-flex-wrap u-gap-medium u-flex-align-center u-margin-top">
            {watch('has_report') && (
              <Button
                type="secondary"
                large
                className="flex-grow-1"
                onClick={() => saveDetailsAndGenerateReport()}>
                {t('admin.candidate.completionModal.generateAutomaticReport')}
              </Button>
            )}
            <Button submit type="primary" large className="flex-grow-1">
              {record.completed_at ? t('common.update') : t('common.complete')}{' '}
              {isSubCheck ? t('common.subCheck') : t('common.check')}
            </Button>
          </div>
        </form>
      )}
    </AnimatedModal>
  );
};
